import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { throttle, get } from "lodash";
import { AccessType } from "@quintype/components";
import DesktopHeader from "./desktop";
import MobileHeader from "./mobile";
import StickyBanner from "../../molecules/sticky-banner";
import { getUserDetails, addTokensToLocalStorage, initializeAuthSync, getAuthCookie, clearAuthCookies, refreshAccessToken, performClientLogout } from "../../helper/utils";
import ElectionFloatingWidget from "../../atoms/election-floating-widget";
import CricketWidget from "../../molecules/cricket-widget";
import { Snackbar } from "../../common/snackbar";
import { createScrollHandler } from "../header/useScrollBehavior.js";
import { useSubscriptionRetry } from "../../pages/my-subscription/useSubscriptionRetry";
import { OnboardModal } from "../../pages/subscribe-page/onboard-modal";
import { requestAccessTypeDhToken } from "../../pages/subscription-page/auth-client.js";
import { RenewalModal } from "../../pages/subscribe-page/renewal-modal";

import {
  setIsLoading,
  setIsLogin,
  setMakeSecondaryApiCallsTriggered,
  setNewNotification,
  setNotificationList,
  setUserDetails,
  updateSubscriberStatus
} from "../../store/actions";
import { SpinnerLoader } from "../../common/loader";
import { GoogleOAuthProvider, googleLogout } from "@react-oauth/google";

import GoogleOneTapSignInUp from "../../atoms/google-one-tap";
import { addAuthTrackingToDataLayer, addSignUpLoginToDataLayer, pushPageDataLayer, pushUserPropertiesUpdateToDataLayer, getAuthStatus } from "../../helper/datalayer-utils";

// Common wrapper for navigation. We could add OffcanvasMenu, Navbar etc components here.
// const hardcodeHeaderURLs = ["/cuisines-of-karnataka", "/media-gallery-new", "/cok-reciepes"];
const NavigationSchema = () => {
  // Get navigation menu data from redux
  const navigationMenu = useSelector((state) =>
    get(state, ["qt", "data", "navigationMenu"], {})
  );

  const schema = useMemo(() => {
    const primaryMenus = navigationMenu?.primaryMenus || [];
    const secondaryMenus = navigationMenu?.secondaryMenus || [];

    // Merge both arrays
    const allMenus = [...primaryMenus, ...secondaryMenus];

    // Convert menu items into schema format
    const hasPart = allMenus
      ?.filter((item) => item?.title && item?.url)
      ?.map((item) => ({
        "@type": "SiteNavigationElement",
        name: item.title,
        url: item.url
      }));

    return {
      "@context": "https://schema.org",
      "@type": "SiteNavigationElement",
      name: "Deccan Herald Main Navigation",
      hasPart
    };
  }, [navigationMenu]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema)
      }}
    />
  );
};

const HeaderInner = ({getSubscriptionForUser, initAccessType}) => {
  const dispatch = useDispatch();
  const [isSSOProcessing, setIsSSOProcessing] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has("access_token") && searchParams.has("refresh_token")) {
        setIsSSOProcessing(true);
      }
    }
  }, []);

  const pageType = useSelector((state) => get(state, ["qt", "pageType"]));
  const pageData = useSelector((state) => get(state, ["qt", "data"], []));
  const isAdfree = useSelector((state) => get(state, ["qt", "config", "subscriber"]) === "true");

  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const isSubscriber = useSelector((state) => get(state, ["qt", "config", "subscriber"], "") === "true");
  const isUserLogin = useSelector((state) => get(state, ["isLogin"], false));
  const ssoEndpoint = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "sso_endpoint"], null)
  );
  const makeSecondaryApiCallsDelay = 2250;
  const [makeSecondaryApiCalls, setMakeSecondaryApiCalls] = useState(false);
  const [isMobile, setMobile] = useState(true);
  // const [isHardcodedHeader, setIsHardcodedHeader] = useState(false);
  const [loadOneTap, setGoogleOneTap] = useState(false);
  const GoogleClientId = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "google_client_id"], "")
  );
  console.log("isUserLogin", { isUserLogin, isSubscriber, userdetails});

  const [showStickyNavbar, setShowStickyNavbar] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState("up");
  const [showPrimaryHeader, setShowPrimaryHeader] = useState(true);
  const [showSecondaryHeader, setShowSecondaryHeader] = useState(true);
  const [showAdBanner, setShowAdBanner] = useState(false);
  const [adClosed, setAdClosed] = useState(false);
  const [showDesktopSecondaryHeader, setShowDesktopSecondaryHeader] = useState(true);
  const [showRedirectSupportStrip, setShowRedirectSupportStrip] = useState(false);
  const SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY = "show_support_strip_on_redirect";
  const [showPaymentProcessingBanner, setShowPaymentProcessingBanner] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(isSubscriber);
  const paymentProcessingActiveRef = useRef(false);
  const { start: startSubscriptionRetry } = useSubscriptionRetry();
  const isOnboardCompleted =
    get(userdetails, ["metadata", "tpml_onboarding_completed"], "") === true;
  const PAYMENT_PROCESSING_STATE_KEY = "subscribe_payment_processing_state";
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const authStatus = getAuthStatus();
      if (authStatus.status === "not_logged_in") {
        pushUserPropertiesUpdateToDataLayer(null, false, "NA", "NA");
      }

      const user = window?.localStorage?.getItem("user");
      if (user && user !== "undefined") {
        try {
          const parsedUser = JSON.parse(user);
          if (parsedUser) {
            addAuthTrackingToDataLayer(parsedUser);
          }
        } catch (e) {
          console.error("Invalid user data in localStorage", e);
        }
      }
    }
  }, []);
  const isHardcodedHeader = useMemo(() => {
    if (
      (pageType === "section-page" || pageType === "sub-section-page") &&
      pageData?.collection?.metadata?.["custom-header"] === "cok2025"
    ) {
      return "cok2025";
    }
    return null;
  }, [pageType, pageData]);

  useEffect(() => {
    if (isUserLogin && typeof getSubscriptionForUser === "function") {
      const email = userdetails?.userData?.emails?.[0] || userdetails?.email;
      const phone = userdetails?.userData?.phoneNumbers?.[0] || userdetails?.phone;

      const ensureAT = async () => {
        if (window.AccessType?.setUser) return true;
        return new Promise(r => {
          let a = 0;
          const c = setInterval(() => {
            if (window.AccessType?.setUser || ++a > 100) {
              clearInterval(c);
              r(!!window.AccessType?.setUser);
            }
          }, 100);
          if (typeof initAccessType === "function") {
            initAccessType(() => {
              if (window.AccessType?.setUser) {
                clearInterval(c);
                r(true);
              }
            });
          }
        });
      };

      const syncSubs = async () => {
        try {
          const isReady = await ensureAT();
          if (isReady && ssoEndpoint) {
            const token = await requestAccessTypeDhToken("");
            if (token) {
              await window.AccessType.setUser({ emailAddress: email, mobileNumber: phone, accesstypeJwt: token });
            }
          }

          const subs = await getSubscriptionForUser();
          let sList = Array.isArray(subs) ? subs : (subs?.subscriptions || []);

          const isSubscriptionActive = (sub) => {
            if (!sub) return false;
            const status = sub.status?.toLowerCase();
            if (status === "active") return true;
            if (status === "cancelled" || sub.cancelled === true) {
              const endTime = sub.end_timestamp || sub.end_date || sub.previous_end_timestamp;
              if (endTime && new Date(endTime).getTime() > Date.now()) return true;
            }
            return false;
          };

          const active = sList.find(isSubscriptionActive);
          if (active) {
            setSubscriptionActive(true);

            const durationUnit = active.duration_unit || "year";
            const durationLength = active.duration_length || 1;
            let planName = `${durationLength}-${durationUnit.toLowerCase().replace(/s$/, "")}`;
            if (planName === "1-month") planName = "monthly";
            const planPrice = active.plan_amount_cents ? (active.plan_amount_cents / 100) : "NA";

            const userUuid = userdetails?.userData?.id || userdetails?.id || "NA";
            if (userUuid !== "NA") {
              sessionStorage.setItem(`active_plan_name_${userUuid}`, planName);
              sessionStorage.setItem(`active_plan_price_${userUuid}`, planPrice);
            }

            pushUserPropertiesUpdateToDataLayer(userdetails, true, planName, planPrice);
          } else {
            pushUserPropertiesUpdateToDataLayer(userdetails, isSubscriber, "NA", "NA");

            const isRecentlyExpired = sList.some(sub => {
              const endTime = sub.end_timestamp || sub.end_date || sub.previous_end_timestamp;
              const status = sub.status?.toLowerCase();
              if (endTime && (status === "expired" || status === "cancelled" || sub.cancelled === true)) {
                const end = new Date(endTime).getTime();
                const now = Date.now();
                const sevenDays = 7 * 24 * 60 * 60 * 1000;
                return (now >= end) && (now - end <= sevenDays);
              }
              return false;
            });

            if (isRecentlyExpired) {
              const userUuid = userdetails?.userData?.id || userdetails?.id || "NA";
              sessionStorage.setItem(`recently_expired_${userUuid}`, "true");
              const today = new Date().toDateString();
              if (localStorage.getItem(`last_renewal_popup_${userUuid}`) !== today) {
                setShowRenewalModal(true);
                localStorage.setItem(`last_renewal_popup_${userUuid}`, today);
              }
            }
          }
        } catch (e) {
          console.warn("[Header] Failed to fetch subscriptions on login", e);
          pushUserPropertiesUpdateToDataLayer(userdetails, isSubscriber, "NA", "NA");
        }
      };

      syncSubs();
    }
  }, [isUserLogin, getSubscriptionForUser, userdetails, ssoEndpoint, isSubscriber, initAccessType]);

  useEffect(() => {
    const isUserDetailsLoaded = Object.keys(userdetails || {}).length > 0;
    const effectivelySubscriber = isSubscriber || subscriptionActive;
    if (isUserLogin && effectivelySubscriber && !isOnboardCompleted && isUserDetailsLoaded) {
      setShowOnboardModal(true);
    } else {
      setShowOnboardModal(false);
    }
  }, [isUserLogin, isSubscriber, subscriptionActive, isOnboardCompleted, userdetails]);

  useEffect(() => {
    document.documentElement.classList.remove("sso-auth-loading");
  }, []);


  const ensureAT = async () => {
    if (window.AccessType?.setUser) return true;
    return new Promise(r => {
      let a = 0;
      const c = setInterval(() => {
        if (window.AccessType?.setUser || ++a > 100) {
          clearInterval(c);
          r(!!window.AccessType?.setUser);
        }
      }, 100);
      if (!isInit.current) {
        isInit.current = true;
        initAccessType(() => {
          if (window.AccessType?.setUser) {
            clearInterval(c);
            r(true);
          }
        });
      }
    });
  };

  const syncUser = async () => {
      const isReady = await ensureAT();
      if (!isReady) return;

      let token;

      try {
        token = await requestAccessTypeDhToken("");
      } catch (e) {
      console.error("[header] Failed to get AccessType token:", e);
        return;
      }

      const email = userdetails?.userData?.emails?.[0] || userdetails?.email;
      const phone = userdetails?.userData?.phone || userdetails?.phone || userdetails?.phoneNumbers?.[0] || userdetails?.mobile || userdetails?.userData?.phoneNumbers?.[0];

      try {
        console.log("[header] Setting user in AccessType:", { email, phone, token });
        await window.AccessType.setUser({ emailAddress: email, mobileNumber: phone, accesstypeJwt: token });
      } catch (e) {
      console.error("[header] window.AccessType.setUser failed:", e);
      }
    };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const shouldShowRedirect = sessionStorage.getItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY) === "1";

    let isProcessingActive = false;
    try {
      const stateStr = sessionStorage.getItem(PAYMENT_PROCESSING_STATE_KEY);
      if (stateStr) {
        const state = JSON.parse(stateStr);
        if (state && state.active) {
          isProcessingActive = true;
        }
      }
    } catch (e) {
      console.warn("Failed to read payment processing state in header:", e);
    }

    if (shouldShowRedirect || isProcessingActive) {
      setShowRedirectSupportStrip(true);
    } else {
      setShowRedirectSupportStrip(false);
    }

    if (shouldShowRedirect) {
      sessionStorage.removeItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY);
    }
  }, [pageType]);

   // Callback to fetch subscriptions for verification - using getSubscriptionForUser from AccessType props
  const fetchSubscription = useCallback(async () => {
    if (typeof getSubscriptionForUser !== "function") return [];
    console.log("[Header] fetchSubscription called");
    try {
      await syncUser();
      const subs = await getSubscriptionForUser();
      console.log("[Header] getSubscriptionForUser result:", subs);
      const subscriptionList = Array.isArray(subs)
        ? subs
        : subs?.subscriptions || [];
      return subscriptionList;
    } catch (e) {
      console.error("[Header] Failed to fetch subscriptions:", e);
      return [];
    }
  }, [getSubscriptionForUser]);

  // Start payment processing verification with retry logic
  const startPaymentProcessingVerification = useCallback(
    (expectedPlanId) => {
      if (paymentProcessingActiveRef.current) {
        // Ensure state is persisted even if already active
        setShowPaymentProcessingBanner(true);
        return;
      }

      // Wait for auth to complete before starting retry logic
      if (!isUserLogin || !userdetails) {
        return;
      }

      paymentProcessingActiveRef.current = true;

      // Show payment processing banner
      setShowPaymentProcessingBanner(true);

      // Show support strip on the next redirected page only for this flow
      if (typeof window !== "undefined") {
        sessionStorage.setItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY, "1");
      }

      // Get expected plan ID from parameter, or fallback to sessionStorage
      let planId = expectedPlanId || null;

      // Persist payment processing state
      if (typeof window !== "undefined") {
        sessionStorage.setItem(
          PAYMENT_PROCESSING_STATE_KEY,
          JSON.stringify({
            active: true,
            startedAt: Date.now(),
            expectedPlanId: planId,
          })
        );
      }
      console.log("[Header] Starting subscription retry with expected plan ID:", planId);
      startSubscriptionRetry({
        fetchSubscription,
        expectedPlanId: planId,

        onSuccess: async (data) => {
          console.log("[Header] Payment verification succeeded. Starting post-verification state update...");
          paymentProcessingActiveRef.current = false;

          // Clear payment processing state
          if (typeof window !== "undefined") {
            sessionStorage.removeItem(PAYMENT_PROCESSING_STATE_KEY);
            sessionStorage.removeItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY);
          }
          setShowPaymentProcessingBanner(false);
          setShowRedirectSupportStrip(false);

          // Send success email
          try {
            if (data) {
              const selectedPlan = data;

              const email = userdetails?.userData?.emails?.[0] || userdetails?.email || "";
              const phone = userdetails?.userData?.phoneNumbers?.[0] || userdetails?.userData?.phone || userdetails?.phone || "";
              const name = userdetails?.metadata?.first_name || userdetails?.name || "";
              const rawAmount = (selectedPlan.plan_amount_cents || 0) / 100;
              const currency = selectedPlan.plan_amount_currency || selectedPlan.payment_amount_currency || selectedPlan.currency || "INR";
              const currencySymbol = currency === "INR" ? "₹" : currency === "USD" ? "$" : "";

              if (email || phone) {
                fetch("/subscription-success-email", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    email,
                    phone,
                    name,
                    plan_name: selectedPlan.plan_name || "",
                    payment_amount: rawAmount,
                    currencySymbol,
                    end_timestamp: selectedPlan?.end_timestamp || "",
                    start_timestamp: selectedPlan?.created_at || "",
                    id: selectedPlan?.subscription_plan_id || ""
                  })
                }).catch((e) => {
                  console.error("[Header] Failed to send subscription success email:", e);
                });
              }
            }
          } catch (e) {
            console.warn("[Header] Failed to send success email:", e);
          }

          // Refresh session token and update user / subscriber profile state
          try {
            console.log("[Header] Token refresh started");
            const freshUser = await refreshAccessToken(() => getUserDetails());
            console.log("[Header] Token refresh completed");

            if (freshUser && !freshUser.errorMessage && !(freshUser instanceof Error)) {
              console.log("[Header] Fresh user details received");
              dispatch(setUserDetails(freshUser));
              if (typeof window !== "undefined") {
                window.localStorage.setItem("user", JSON.stringify(freshUser));
              }

              // Update Redux subscriber state
              console.log("[Header] Redux subscriber state updated");
              dispatch(updateSubscriberStatus(true));

              // Determine onboarding status from the fresh user object
              const onboardingCompleted = get(freshUser, ["metadata", "tpml_onboarding_completed"], "") === true;
              console.log("[Header] Onboarding status evaluated:", onboardingCompleted);
              if (!onboardingCompleted) {
                setShowOnboardModal(true);
              }
            } else {
              console.error("[Header] Refreshed user profile was invalid or returned an error:", freshUser);
            }
          } catch (error) {
            console.error("[Header] Failed to refresh user profile or update subscriber status:", error);
          }

          setSubscriptionActive(true);
        },

        onTimeout: async () => {
          console.warn("[Header] Subscription retry timed out, clearing payment processing state");
          paymentProcessingActiveRef.current = false;

          if (typeof window !== "undefined") {
            sessionStorage.removeItem(PAYMENT_PROCESSING_STATE_KEY);
            sessionStorage.removeItem("subscriptionData");
            sessionStorage.removeItem("isSubscribed");
            sessionStorage.setItem("user_paid_status", "unpaid");
            sessionStorage.removeItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY);
          }

          setShowPaymentProcessingBanner(false);
          setShowRedirectSupportStrip(false);
        },
      });
    },
    [
      fetchSubscription,
      setShowPaymentProcessingBanner,
      startSubscriptionRetry,
      isUserLogin,
      userdetails,
    ]
  );

  // Check for payment processing state and initiate retry logic
  useEffect(() => {
    if (typeof window === "undefined" || !isUserLogin) return;

    try {
      const stateStr = sessionStorage.getItem(PAYMENT_PROCESSING_STATE_KEY);
      if (stateStr) {
        const state = JSON.parse(stateStr);
        if (state && state.active && !paymentProcessingActiveRef.current) {
          console.log("[Header] Found active payment processing state, starting verification");
          startPaymentProcessingVerification(state.expectedPlanId);
        }
      }
    } catch (e) {
      console.warn("[Header] Failed to check payment processing state:", e);
    }
  }, [isUserLogin, startPaymentProcessingVerification]);

  const getScroll = createScrollHandler({
    lastScrollY,
    makeSecondaryApiCalls,
    adClosed,
    setMakeSecondaryApiCalls,
    setShowPrimaryHeader,
    setShowSecondaryHeader,
    setShowAdBanner,
    setShowStickyNavbar,
    setShowDesktopSecondaryHeader,
    setScrollDirection,
    setLastScrollY,
    showPrimaryHeader,
    showSecondaryHeader,
    showAdBanner,
    showStickyNavbar,
    showDesktopSecondaryHeader
  });

  useEffect(() => {
    if (window && window?.innerWidth < 1025) {
      setMobile(true);
    } else {
      setMobile(false);
      setMakeSecondaryApiCalls(true);
    }

    if (window) {
      initializeAuthSync();

      const decodedSearch = decodeURIComponent(window.location.search || "");
      const rawSearch = decodedSearch.replace(/\?/g, "&").replace("&", "?");
      const searchParamsObj = new URLSearchParams(rawSearch);
      const access_token = searchParamsObj.get("access_token");
      const refresh_token = searchParamsObj.get("refresh_token");
      const userLocalStorage = localStorage.getItem("user");
      const stored_access_token = getAuthCookie("st-access-token");
      const stored_refresh_token = getAuthCookie("st-refresh-token");

      console.log("[COOKIE SYNC][HEADER] Initialized sync. Cookies present:", {
        hasAccessToken: !!stored_access_token,
        hasRefreshToken: !!stored_refresh_token
      });

      if (stored_refresh_token) {
        if (stored_access_token) {
          console.log("[COOKIE SYNC][HEADER] Cookies found, user logged in. Dispatching setIsLogin(true)");
          dispatch(setIsLogin(true));
          if (!userLocalStorage || userLocalStorage === "undefined" || userLocalStorage.trim() === "") {
            console.log("[COOKIE SYNC][HEADER] Local user details missing. Fetching from backend...");
            getUserDetails()
              .then((data) => {
                if (data && data.status !== 401) {
                  dispatch(setUserDetails(data || {}));
                  window.localStorage.setItem("user", JSON.stringify(data));
                } else if (data?.status === 401) {
                  console.log("[COOKIE SYNC][HEADER] Cookie-based user details returned 401. Clearing tokens and logging out.");
                  performClientLogout(dispatch);
                  googleLogout();
                  window.location.href = `${ssoEndpoint}/auth/logout?redirectUrl=${window.location.href.split("?")[0]}`;
                }
              })
              .catch((err) => console.error("[COOKIE SYNC][HEADER] Error fetching user details:", err));
          } else {
            try {
              const parsedUser = JSON.parse(userLocalStorage);
              if (parsedUser) {
                console.log("[COOKIE SYNC][HEADER] Restoring user from localStorage to Redux");
                dispatch(setUserDetails(parsedUser));
              }
            } catch (e) {
              console.error("[COOKIE SYNC][HEADER] Failed to parse userLocalStorage on mount:", e);
              console.log("[COOKIE SYNC][HEADER] Falling back to fetch user details from backend...");
              getUserDetails()
                .then((data) => {
                  if (data && data.status !== 401) {
                    dispatch(setUserDetails(data || {}));
                    window.localStorage.setItem("user", JSON.stringify(data));
                  }
                })
                .catch((err) => console.error("[COOKIE SYNC][HEADER] Error fetching fallback user details:", err));
            }
          }
        } else {
          console.log("[COOKIE SYNC][HEADER] Refresh token found but access token missing. Proactively refreshing token...");
          refreshAccessToken(() => getUserDetails())
            .then((data) => {
              if (data && data.status !== 401 && !(data instanceof Error)) {
                dispatch(setIsLogin(true));
                dispatch(setUserDetails(data || {}));
                window.localStorage.setItem("user", JSON.stringify(data));
              }
            })
            .catch((err) => console.error("[COOKIE SYNC][HEADER] Auto-refresh failed during initial sync:", err));
        }
      }
      if (access_token && refresh_token) {
        console.log("[COOKIE SYNC][HEADER] New tokens found in URL, syncing them...");
        addTokensToLocalStorage(access_token, refresh_token);
        getUserDetails()
          .then((data) => {
            if (data?.status === 401) {
              console.log("[COOKIE SYNC][HEADER] Fresh user details returned 401. Clearing tokens and logging out.");
              performClientLogout(dispatch);
              googleLogout();

              let redirectUrl = window.location.origin + "/";
              const urlParams = new URLSearchParams(window.location.search);
              const returnUrl = urlParams.get("returnUrl");

              if (returnUrl) {
                redirectUrl = returnUrl;
              } else if (document.referrer && document.referrer.includes(window.location.origin)) {
                redirectUrl = document.referrer;
              }

              setTimeout(() => {
                window.location.search = "";
                window.location.href = `${ssoEndpoint}/auth/logout?redirectUrl=${redirectUrl}`;
              }, 300);
            } else {
              sessionStorage.setItem("skip_auth_tracking", "true");
              dispatch(setIsLogin(true));
              dispatch(setUserDetails(data || {}));
              localStorage.setItem("user", JSON.stringify(data));
              searchParamsObj.delete("access_token");
              searchParamsObj.delete("refresh_token");

              const searchString = searchParamsObj.toString();
              const finalSearchParam = searchString ? `?${searchString}` : "";

              addSignUpLoginToDataLayer(window.location.href, data);
              
              // Hard reload the page so that SSR fetches the premium/unlocked content (removes paywall and ads).
              window.location.replace(window.location.pathname + finalSearchParam);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      } else if (stored_access_token && stored_refresh_token) {
         console.log("[COOKIE SYNC][HEADER] Tokens exist. Setting setGoogleOneTap(false)");
         setGoogleOneTap(false);
         if (userLocalStorage && userLocalStorage !== "undefined" && userdetails?.userData === undefined) {
           if (typeof userLocalStorage === "string" && userLocalStorage?.trim() !== "") {
             try {
               dispatch(setUserDetails(JSON.parse(userLocalStorage)));
             } catch(e) {
               console.error("[COOKIE SYNC][HEADER] Failed to parse userLocalStorage:", e);
             }
           }
         }
      } else {
        console.log("[COOKIE SYNC][HEADER] Checking tokens for Google One Tap. stored_access_token:", !!stored_access_token, "stored_refresh_token:", !!stored_refresh_token);
        if (stored_access_token || stored_refresh_token) {
          console.log("[COOKIE SYNC][HEADER] Tokens exist. Setting setGoogleOneTap(false)");
          setGoogleOneTap(false);
        } else {
          console.log("[COOKIE SYNC][HEADER] Tokens missing. Setting setGoogleOneTap(true)");
          setGoogleOneTap(true);
        }
      }
    }
  }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          getScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [adClosed, lastScrollY, makeSecondaryApiCalls, showPrimaryHeader, showSecondaryHeader, showAdBanner, showStickyNavbar, showDesktopSecondaryHeader]);

  useEffect(() => {
    makeSecondaryApiCalls &&
      dispatch(setMakeSecondaryApiCallsTriggered(makeSecondaryApiCalls, makeSecondaryApiCallsDelay, isMobile));
  }, [makeSecondaryApiCalls]);

  useEffect(() => {
    _izq.push([
      "getNotificationFeed",
      function (response) {
        if (response?.response !== "success") {
          dispatch(setIsLoading(true));
        } else {
          dispatch(setIsLoading(false));
          dispatch(setNotificationList(response.data));
          const latestNotificationCreatedAt = window?.localStorage?.getItem("latestNotificationCreatedAt");

          if (response.data[0]?.createdAt !== latestNotificationCreatedAt || latestNotificationCreatedAt == null) {
            dispatch(setNewNotification(true));
          } else {
            dispatch(setNewNotification(false));
          }
        }
      }
    ]);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    let changed = false;
    const utmMedium = params.get("utm_medium");

    if (utmMedium === "brick-wall" || utmMedium === "floatingwidget") {
      params.delete("utm_medium");
      params.delete("utm_campaign");
      changed = true;
    }
    if (changed) {
      const newUrl = url.pathname + (params.toString() ? `?${params.toString()}` : "") + url.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const dataLayerPushedRef = useRef(false);

  useEffect(() => {
    dataLayerPushedRef.current = false;
  }, [pageType]);

  useEffect(() => {
    if (pageType && !dataLayerPushedRef.current) {
      const timer = setTimeout(() => {
        if (!dataLayerPushedRef.current) {
          pushPageDataLayer(pageType, userdetails, pageData);
          dataLayerPushedRef.current = true;
        }
      }, 100); // Reduced timeout since we now read from localStorage directly

      return () => clearTimeout(timer);
    }
  }, [pageType, pageData]);

  //  useEffect(() => {
  //   if (typeof document !== "undefined" && typeof window !== "undefined") {
  //     // Skip padding for custom headers - they handle their own spacing
  //     if (isHardcodedHeader) {
  //       document.body.style.paddingTop = "0px";
  //       document.body.style.paddingBottom = "0px";
  //       return;
  //     }

  //     const isMobileView = window.innerWidth < 769;
  //     if (isMobileView) {
  //       // Always reserve space for bottom elements to prevent CLS
  //       // This space is reserved whether elements are visible or not
  //       let bottomPadding = 0;
  //       if (showStickyNavbar && showAdBanner) {
  //         bottomPadding = 120; // navbar + banner
  //       } else if (showStickyNavbar) {
  //         bottomPadding = 52; // navbar only
  //       } else if (showAdBanner) {
  //         bottomPadding = 68; // banner only
  //       } else {
  //         bottomPadding = 0; // nothing visible
  //       }
  //       document.body.style.paddingBottom = `${bottomPadding}px`;

  //       // Reserve space for top headers
  //       let topPadding = 0;
  //       if (showPrimaryHeader && showSecondaryHeader) {
  //         topPadding = 83;
  //       } else if (showSecondaryHeader) {
  //         topPadding = 36;
  //       } else {
  //         topPadding = 0;
  //       }
  //       document.body.style.paddingTop = `${topPadding}px`;
  //     } else {
  //       document.body.style.paddingBottom = "0px";
  //       let desktopTopPadding = 75;
  //       if (showDesktopSecondaryHeader) {
  //         desktopTopPadding += 46;
  //       }
  //       document.body.style.paddingTop = `${desktopTopPadding}px`;
  //     }
  //   }
  //   return () => {
  //     if (typeof document !== "undefined") {
  //       document.body.style.paddingBottom = "0px";
  //       document.body.style.paddingTop = "0px";
  //       document.body.style.transition = "";
  //     }
  //   };
  // }, [showStickyNavbar, showPrimaryHeader, showSecondaryHeader, showDesktopSecondaryHeader, showAdBanner, isHardcodedHeader]);

  return (
    <GoogleOAuthProvider clientId={GoogleClientId}>
      {isSSOProcessing && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 999999, backgroundColor: "#ffffff" }}>
          <SpinnerLoader />
        </div>
      )}
      <NavigationSchema />
      {loadOneTap && <GoogleOneTapSignInUp />}
      {!isMobile ? (
        <div className="header-hide-tab">
          <DesktopHeader isHardcodedHeader={isHardcodedHeader} showSecondaryHeader={showDesktopSecondaryHeader} showRedirectSupportStrip={showRedirectSupportStrip || showPaymentProcessingBanner} isSubscriber={isSubscriber || subscriptionActive} />
        </div>
      ) : (
        <div className="header-hide-dektop">
          <MobileHeader isHardcodedHeader={isHardcodedHeader}
          showPrimaryHeader={showPrimaryHeader}
          showSecondaryHeader={showSecondaryHeader}
          scrollDirection={scrollDirection}
          showStickyNavbar={showStickyNavbar}
          isSubscriber={isSubscriber || subscriptionActive}
          showRedirectSupportStrip={showRedirectSupportStrip || showPaymentProcessingBanner} />
        </div>
      )}
      {isMobile && <StickyBanner isVisible={!isSubscriber && showAdBanner} showStickyNavbar={showStickyNavbar} />}
      <ElectionFloatingWidget />
      <CricketWidget />
      <Snackbar />
      {showOnboardModal && <OnboardModal onClose={() => setShowOnboardModal(false)} />}
      {showRenewalModal && <RenewalModal onClose={() => setShowRenewalModal(false)} />}
    </GoogleOAuthProvider>
  );
};

const Header = () => {
  const qt = useSelector((state) => get(state, ["qt"], {}));
  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const isUserLogin = useSelector((state) => get(state, ["isLogin"], false));

  // Get AccessType configuration from Redux, similar to SubscriptionPage
  const ck = get(qt, ["config", "publisher-attributes", "subscription_setup"], {});
  const aKey = ck.accesstype_key || get(qt, ["config", "publisher-attributes", "accesstype_key"], "");

  // Only pass user data if logged in
  const email = userdetails?.userData?.emails?.[0] || userdetails?.email || "";
  const phone = userdetails?.userData?.phoneNumbers?.[0] || userdetails?.userData?.phone || userdetails?.phone || "";
  console.log("[Header] AccessType config:", { aKey, email, phone, isUserLogin });

  return (
    <AccessType
      accessTypeKey={aKey}
      accessTypeBkIntegrationId={ck.accesstype_subauth || ""}
      enableAccesstype={true}
      email={email}
      phone={phone}
    >
      {(props) => <HeaderInner {...props} />}
    </AccessType>
  );
};

export { Header };
